import React, { useEffect, useState } from "react";
import { X, HelpCircle, ExternalLink, PenLine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchQuestionsForVerse } from "@/lib/api";

/**
 * variant: "sheet" (mobile bottom sheet) | "panel" (desktop right side)
 */
export default function QuestionSheet({ book, chapter, verse, onClose, variant = "sheet" }) {
    const [questions, setQuestions] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        fetchQuestionsForVerse(book, chapter, verse).then(d => setQuestions(d.questions || []));
    }, [book, chapter, verse]);

    const bookName = book === "Mat" ? "Matthew" : book;

    const reflect = (q) => {
        const uid = `n_${Math.random().toString(36).slice(2, 10)}`;
        sessionStorage.setItem("pending-question-card", JSON.stringify({
            node_id: uid,
            reference: `${book}.${chapter}.${verse}`,
            refs: [`${book}.${chapter}.${verse}`],
            label: `${bookName} ${chapter}:${verse}`,
            translation: "kjv",
            text: q.question,
            format: "full",
        }));
        onClose?.();
        nav("/journal/new?fromQuestion=1");
    };

    const body = (
        <>
            {!isPanel(variant) && (
                <div className="pt-2 pb-1 flex justify-center">
                    <div className="w-10 h-1.5 rounded-full bg-ink-10" />
                </div>
            )}
            <div className={"px-6 border-b border-ink-10 flex items-center justify-between " + (isPanel(variant) ? "pt-6 pb-3" : "py-3")}>
                <div className="flex items-center gap-2">
                    <HelpCircle size={14} className="text-moss-deep" />
                    <span className="font-ui text-[11px] uppercase tracking-[0.2em] text-moss-deep font-semibold">
                        {bookName} {chapter}:{verse} · QuestionGod
                    </span>
                </div>
                <button
                    data-testid="close-question-sheet"
                    onClick={onClose}
                    className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-ink-10 transition"
                    aria-label="Close"
                >
                    <X size={18} />
                </button>
            </div>
            <div className="px-6 py-5 overflow-y-auto sheet-scroll flex-1">
                {questions === null && <p className="font-ui text-sm text-ink-40">Loading…</p>}
                {questions !== null && questions.length === 0 && (
                    <p className="font-ui text-sm text-ink-40">No questions recorded for this verse.</p>
                )}
                {questions && questions.map((q, i) => (
                    <article key={q.id} data-testid={`question-${q.id}`} className={i > 0 ? "mt-8 pt-6 border-t border-ink-10" : ""}>
                        <h2 className="scripture text-2xl md:text-3xl text-ink leading-snug tracking-tight">
                            "{q.question}"
                        </h2>
                        {q.pericope && (
                            <p className="mt-2 font-ui text-[12px] text-ink-40 italic">{q.pericope}</p>
                        )}
                        {(q.asked_by || q.asked_to || q.question_type) && (
                            <dl
                                data-testid={`meta-${q.id}`}
                                className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3"
                            >
                                {q.asked_by && (
                                    <MetaField testid={`meta-asker-${q.id}`} label="Asked by" value={q.asked_by} />
                                )}
                                {q.asked_to && (
                                    <MetaField testid={`meta-audience-${q.id}`} label="Asked to" value={q.asked_to} />
                                )}
                                {q.question_type && (
                                    <MetaField testid={`meta-type-${q.id}`} label="Type of question" value={q.question_type} />
                                )}
                            </dl>
                        )}
                        <div className="mt-4 flex flex-wrap gap-2">
                            <button
                                data-testid={`reflect-${q.id}`}
                                onClick={() => reflect(q)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-moss text-white font-ui text-[12px] font-medium hover:bg-moss-deep transition"
                            >
                                <PenLine size={12} /> Reflect in journal
                            </button>
                            {q.source_url && (
                                <a
                                    data-testid={`source-${q.id}`}
                                    href={q.source_url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] text-ink hover:border-moss/40 transition"
                                >
                                    <ExternalLink size={12} /> View on QuestionGod.org
                                </a>
                            )}
                        </div>

                        <QgSection testid={`purpose-${q.id}`} title="Purpose" body={q.purpose} />
                        <QgSection testid={`response-${q.id}`} title="Response" body={q.response} />
                        <QgSection testid={`commentary-${q.id}`} title="Commentary" body={q.commentary} />
                        <QgSection testid={`application-${q.id}`} title="Practical Application" body={q.practical_application} />

                        {!(q.purpose || q.response || q.commentary || q.practical_application) && (
                            <p
                                data-testid={`no-enrichment-${q.id}`}
                                className="mt-6 font-ui text-[12px] text-ink-40 italic"
                            >
                                {q.source_url
                                    ? <>Deeper commentary for this question isn't in our library yet — tap<span className="mx-1 font-medium not-italic text-moss-deep">View on QuestionGod.org</span>to explore purpose, response, commentary, and practical application.</>
                                    : <>This is a reflective prompt from the reader. Take it to your journal, or explore related questions on <a className="not-italic underline text-moss-deep" href="https://www.questiongod.org/nt/matthew/" target="_blank" rel="noreferrer noopener">QuestionGod.org</a>.</>}
                            </p>
                        )}
                    </article>
                ))}
            </div>
        </>
    );

    if (isPanel(variant)) {
        return <div data-testid="question-sheet" className="flex flex-col h-full bg-white">{body}</div>;
    }
    return (
        <div data-testid="question-sheet" className="fixed inset-0 z-50 animate-fade-in lg:hidden">
            <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
            <div className="absolute inset-x-0 bottom-0 max-w-md md:max-w-lg mx-auto bg-white rounded-t-3xl shadow-[0_-8px_32px_rgba(30,43,38,0.18)] animate-sheet-in flex flex-col max-h-[85vh]">
                {body}
            </div>
        </div>
    );
}

function isPanel(v) { return v === "panel"; }

/**
 * Compact labeled meta pair (label on top, value on the pill below) — makes it
 * obvious which chip is the asker vs the audience vs the question type.
 */
function MetaField({ testid, label, value }) {
    return (
        <div data-testid={testid} className="flex flex-col gap-1 min-w-0">
            <dt className="font-ui text-[10px] uppercase tracking-[0.18em] text-ink-40 font-semibold">
                {label}
            </dt>
            <dd className="inline-flex self-start max-w-full px-2.5 py-1 rounded-full bg-sage-paper text-ink font-ui text-[12px] font-medium leading-snug break-words">
                {value}
            </dd>
        </div>
    );
}

/**
 * Renders a titled section (Purpose / Response / Commentary / Practical Application).
 * Skips rendering entirely if the body is empty.
 * Inline `[Ref](https://ref.ly/...)` links are converted to plain-text refs to avoid
 * dragging the QuestionGod link out of the sheet — the "View on QuestionGod.org"
 * button is the single point of egress.
 */
function QgSection({ testid, title, body }) {
    if (!body) return null;
    // Convert markdown-style ref.ly links to plain-text scripture citations.
    const cleaned = String(body).replace(/\[([^\]]+)\]\(https?:\/\/[^)]+\)/g, "$1");
    return (
        <section data-testid={testid} className="mt-6">
            <h3 className="font-ui text-[10px] uppercase tracking-[0.25em] text-moss-deep font-semibold">
                {title}
            </h3>
            <p className="mt-2 scripture text-[15px] md:text-[16px] leading-[1.75] text-ink-70 whitespace-pre-line">
                {cleaned}
            </p>
        </section>
    );
}
